import { FileDto, FolderDto, AssignedTo } from "@green0meter/api-client";
import { Modal } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { array, number, object, string } from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ASSIGN_TO_VALUES } from "@type/User";
import { Chip } from "@components/common";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@components/base";
import { EDITABLE_FILE_EXTENSIONS } from "@type/File";
import { useUserStore } from "@store/user";
import { useGetUsers } from "@api";

export interface DocumentTypeOption {
  id: string;
  label: string;
  ext: string;
}

type DocumentModalMode = "create" | "edit";
interface CreateEditDocumentModal {
  open: boolean;
  onClose: () => void;
  onConfirm?: (
    documentName: string,
    documentType: DocumentTypeOption,
    assignedTo: AssignedTo,
    folderId: string,
    userIds: string[],
  ) => void;
  onConfirmEdit?: (
    documentId: string,
    documentName: string,
    documentType: DocumentTypeOption,
    assignedTo: AssignedTo,
    folderId: string,
    userIds: string[],
  ) => void;
  documentTypes: DocumentTypeOption[];
  isLoading?: boolean;
  onCreateFolderClick: () => void;
  mode?: DocumentModalMode;
  documentMeta?: FileDto | null;
  uploadedFile?: File;
  folder?: FolderDto | null;
  rootFolder: FolderDto | null;
}

interface CreateEditDocumentFormValues {
  documentName: string;
  documentTypeId?: string;
  assignedTo: AssignedTo;
  folderId: string;
  userIds: string[];
}

export const getDocumentTypeIdFromFile = (
  file: FileDto,
  documentTypes: DocumentTypeOption[],
): string => {
  const ext = file.name.split(".").pop()?.toUpperCase();

  if (!ext) return documentTypes[0]?.id ?? "";

  const typeEntry = Object.entries(EDITABLE_FILE_EXTENSIONS).find(
    ([type, extensions]) => extensions.includes(ext),
  );

  return typeEntry ? typeEntry[0] : documentTypes[0]?.id ?? "";
};

const getDocumentTypeIdFromRawFile = (
  file: File,
  documentTypes: DocumentTypeOption[],
): string => {
  const ext = file.name.split(".").pop()?.toUpperCase();

  if (!ext) return documentTypes[0]?.id ?? "";

  const typeEntry = Object.entries(EDITABLE_FILE_EXTENSIONS).find(
    ([type, extensions]) => extensions.includes(ext),
  );

  return typeEntry ? typeEntry[0] : documentTypes[0]?.id ?? "";
};

const getBaseFileName = (name: string): string => {
  const parts = name.split(".");

  if (parts.length === 1) return name;

  return parts.slice(0, -1).join(".");
};

const CreateEditDocumentModal: FC<CreateEditDocumentModal> = ({
  open,
  onClose,
  onConfirm,
  onConfirmEdit,
  documentTypes,
  isLoading = false,
  onCreateFolderClick,
  mode = "create",
  documentMeta,
  uploadedFile,
  folder,
  rootFolder,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { users } = useGetUsers(user, {
    companyId: user?.activeCompanyId,
  });

  const schema = object({
    documentName: string()
      .trim()
      .min(1, t("validation.required"))
      .required(t("validation.required")),
    documentTypeId: string().optional(),
    assignedTo: number().required(),
    folderId: string().required(t("validation.required")),
    userIds: array().of(string().required()).default([]),
  });

  const folderOptions =
    rootFolder?.children
      ?.filter((child) => !child.isRoot)
      .map((child) => ({
        id: child.id,
        name: child.name,
      })) ?? [];

  const defaultValues: CreateEditDocumentFormValues =
    mode === "edit" && documentMeta
      ? {
          documentName: getBaseFileName(documentMeta.name),
          documentTypeId: getDocumentTypeIdFromFile(documentMeta, documentTypes),
          assignedTo: documentMeta.assignment?.assignedTo ?? AssignedTo.OnlyOwner,
          folderId: documentMeta.folderId ?? folder?.id ?? folderOptions[0]?.id,
          userIds: Array.isArray(documentMeta?.assignment?.userIds)
            ? documentMeta.assignment.userIds
            : [],
        }
      : uploadedFile
        ? {
            documentName: getBaseFileName(uploadedFile.name),
            documentTypeId: getDocumentTypeIdFromRawFile(
              uploadedFile,
              documentTypes,
            ),
            assignedTo: AssignedTo.OnlyOwner,
            folderId: folder?.id ?? folderOptions[0]?.id,
            userIds: [],
          }
        : {
            documentName: "",
            documentTypeId: documentTypes[0]?.id ?? "",
            assignedTo: AssignedTo.OnlyOwner,
            folderId: folder?.id ?? folderOptions[0]?.id,
            userIds: [],
          };
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<CreateEditDocumentFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const assignedTos = Object.entries(ASSIGN_TO_VALUES).filter(
    ([id]) => Number(id) !== AssignedTo.Mixed,
  );

  const onSubmit = (values: CreateEditDocumentFormValues): void => {
    const type = documentTypes.find((t) => t.id === values.documentTypeId)!;

    if (mode === "edit" && documentMeta && onConfirmEdit) {
      onConfirmEdit(
        documentMeta.id,
        `${values.documentName.trim()}${type.ext}`,
        type,
        values.assignedTo,
        values.folderId,
        values?.userIds ?? [],
      );
    } else if (mode === "create" && onConfirm) {
      onConfirm(
        `${values.documentName.trim()}${type.ext}`,
        type,
        values.assignedTo,
        values.folderId,
        values?.userIds ?? [],
      );
    }
    reset();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
    >
      <Box
        p={4}
        maxWidth={500}
        mx="auto"
        my="20vh"
        borderRadius={2}
        boxShadow={3}
        bgcolor="background.paper"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">
            {mode === "edit"
              ? t("createDocumentModal.editTitle")
              : t("createDocumentModal.title")}
          </Typography>
          <Button variant="text" size="small" onClick={onClose}>
            <FeatherIcon icon="x" />
          </Button>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <Controller
            control={control}
            name="documentName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("createDocumentModal.documentName")}
                fullWidth
                disabled={isLoading}
                error={!!error}
                helperText={error?.message}
                autoFocus
                margin="normal"
              />
            )}
          />
          {!uploadedFile && (
            <Controller
              control={control}
              name="documentTypeId"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label={t("createDocumentModal.chooseDocumentType")}
                  fullWidth
                  disabled={isLoading}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  margin="normal"
                >
                  {documentTypes.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}

          <Controller
            control={control}
            name="folderId"
            render={({ field, fieldState: { error } }) => (
              <Box mb={2} display="flex" alignItems="baseline" gap={1}>
                <TextField
                  {...field}
                  select
                  label={t("createDocumentModal.chooseFolder")}
                  fullWidth
                  disabled={isLoading}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  margin="normal"
                >
                  <MenuItem value={folder?.id}>
                    {t("createDocumentModal.noFolder")}
                  </MenuItem>
                  {folderOptions.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="text"
                  size="small"
                  onClick={onCreateFolderClick}
                  title={t("createDocumentModal.createFolder")}
                  disabled={isLoading}
                >
                  <FeatherIcon icon="plus" />
                </Button>
              </Box>
            )}
          />
          <Controller
            control={control}
            name="userIds"
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth margin="normal" error={!!error}>
                <InputLabel>{t("createDocumentModal.selectUsers")}</InputLabel>
                <Select
                  {...field}
                  multiple
                  label={t("createDocumentModal.selectUsers")}
                  value={field.value ?? []}
                  onChange={(event) => field.onChange(event.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((userId) => {
                        const user = users?.items.find((u) => u.id === userId);

                        // TODO: fix onDelete behavior
                        return (
                          <Chip
                            key={userId}
                            label={
                              user ? `${user.firstName} ${user.lastName}` : userId
                            }
                            size="small"
                            type={""}
                          />
                        );
                      })}
                    </Box>
                  )}
                  size="small"
                >
                  {users?.items.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography color="error" variant="caption">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="assignedTo"
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth margin="normal" error={!!error}>
                <InputLabel>{t("createFolderModal.assignTo")}</InputLabel>
                <Select
                  {...field}
                  label={t("createFolderModal.assignTo")}
                  size="small"
                  renderValue={(selected) => (
                    <Chip
                      label={t(`assignTo.${assignedTos[selected as AssignedTo][1]}`)}
                      type={selected as AssignedTo}
                      size="small"
                    />
                  )}
                >
                  {assignedTos.map(([id, key]) => (
                    <MenuItem value={Number(id)} key={id}>
                      <Chip
                        label={t(`assignTo.${key}`)}
                        type={Number(id)}
                        size="small"
                      />
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography color="error" variant="caption">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!isValid || isLoading}
            >
              {mode === "edit" ? t("common.save") : t("common.create")}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateEditDocumentModal;
